// CrazyGames login orchestration (portal-gated). itch.io / direct never import the
// effects of this file: every entry point bails unless running inside CrazyGames, so
// the username/password login is completely untouched there.
//
// Flow (per CrazyGames account-integration docs):
//  - On start: if the CrazyGames user is logged in → verify + auto-login (linking or
//    creating a backend account); otherwise start an ephemeral per-device GUEST session
//    so the player can always play.
//  - An auth listener catches a guest logging into CrazyGames mid-session and migrates
//    their in-progress guest account in place (progress preserved), then reloads.
import { useAuthStore } from '../stores/authStore';
import { isCrazyGames } from './portal';
import {
  initCrazyGames,
  isUserAccountAvailable,
  getCrazyUser,
  getCrazyUserToken,
  showCrazyAuthPrompt,
  addCrazyAuthListener,
} from './crazygames';

const DEVICE_KEY = 'cg_device_id';

let bootstrapped = false;
let linking = false; // guards against concurrent login/migration (listener + prompt)

function genDeviceId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `g-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

// Existing device id without creating one (used when migrating a prior guest).
function currentDeviceId(): string | undefined {
  return localStorage.getItem(DEVICE_KEY) || undefined;
}

// Ensure a device id exists (guest key). Created lazily on first guest session.
function ensureDeviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) { id = genDeviceId(); localStorage.setItem(DEVICE_KEY, id); }
  return id;
}

// After a real CrazyGames login, the current device's guest slot has been migrated (or
// abandoned): drop the device id so a later guest on this device gets a fresh account
// and never resumes the now-linked one.
function clearDeviceId() { localStorage.removeItem(DEVICE_KEY); }

// Start (or resume) a guest session. Retries once with a fresh device id if the stored
// one was already claimed by a linked account.
async function startGuest(): Promise<void> {
  const store = useAuthStore.getState();
  let res = await store.crazyGamesGuest(ensureDeviceId());
  if (res.guestClaimed) {
    clearDeviceId();
    res = await store.crazyGamesGuest(ensureDeviceId());
  }
}

// Log in with the current CrazyGames user token, migrating the current guest (if any).
async function loginWithCrazyToken(): Promise<boolean> {
  if (linking) return false;
  linking = true;
  try {
    const token = await getCrazyUserToken();
    if (!token) return false;
    const res = await useAuthStore.getState().crazyGamesLogin(token, currentDeviceId());
    if (res.success) { clearDeviceId(); return true; }
    return false;
  } finally {
    linking = false;
  }
}

// Called when a guest logs into CrazyGames mid-session: link+migrate, then reload so the
// whole app re-bootstraps cleanly under the linked account.
async function onGuestLoggedIn(): Promise<void> {
  if (await loginWithCrazyToken()) window.location.reload();
}

// App bootstrap on CrazyGames: decide login vs guest, register the auth listener. Safe
// to call once at startup; a no-op off CrazyGames.
export async function bootstrapCrazyGamesAuth(): Promise<void> {
  if (!isCrazyGames || bootstrapped) return;
  bootstrapped = true;
  try {
    await initCrazyGames(); // ensure the SDK is ready
    addCrazyAuthListener(() => { void onGuestLoggedIn(); });

    const available = await isUserAccountAvailable();
    const user = available ? await getCrazyUser() : null;
    if (user && (await loginWithCrazyToken())) return;

    await startGuest();
  } catch {
    // Best-effort fall back to a guest session.
    try { await startGuest(); } catch { /* ignore */ }
  } finally {
    // Never leave the app hanging on "loading" if every attempt failed.
    if (!useAuthStore.getState().authChecked) useAuthStore.setState({ authChecked: true });
  }
}

// "Log in with CrazyGames" button: open the prompt; the auth listener then performs the
// link+migrate+reload. Falls back to an explicit login if the listener didn't fire.
export async function promptCrazyGamesLogin(): Promise<void> {
  if (!isCrazyGames) return;
  await initCrazyGames();
  const opened = await showCrazyAuthPrompt();
  if (!opened) return;
  await onGuestLoggedIn();
}
